import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { LogOut, Menu, Monitor, UserRound, X } from 'lucide-react';
import { ActivityBell } from '../../components/activity/ActivityBell';
import { ActivityListModal } from '../../components/activity/ActivityListModal';
import { Logo } from '../../components/brand/Logo';
import { tierLabel } from '../../lib/auth/useSession';
import { cx } from '../../lib/util/cx';
import { logout } from '../../services/authService';
import type { Profile } from '../../types/profile';
import type { NavSection } from './nav';

/**
 * The shell on a small screen (see `smallScreen.ts`): a sticky header — page
 * title, the activity bell, and the menu button at the far right — and the
 * nav as a drawer from the right. The drawer lists every screen the roles can
 * read; the ones not turned on for phones (`NavItem.mobile`) are shown
 * disabled, so the menu still says what exists.
 */
export function MobileHeader({
  breadcrumb,
  title,
  sections,
  activeId,
  profile,
}: {
  breadcrumb?: string;
  title?: string;
  sections: NavSection[];
  activeId?: string;
  profile: Profile;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  const closeMenu = () => {
    setMenuOpen(false);
    menuButton.current?.focus();
  };

  return (
    <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b border-[rgba(143,68,32,.1)] bg-[rgba(241,237,225,.92)] pl-4 pr-2 font-body backdrop-blur-[14px]">
      <div className="min-w-0 flex-1">
        {breadcrumb && (
          <div className="text-[10px] font-semibold uppercase tracking-[.14em] text-muted">{breadcrumb}</div>
        )}
        <div className="truncate font-heading text-[17px] font-bold tracking-[-.01em] text-brown-deep">
          {title ?? 'HomePlated Admin'}
        </div>
      </div>
      <ActivityBell onClick={() => setActivityOpen(true)} />
      <button
        ref={menuButton}
        type="button"
        aria-label="Open menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(true)}
        className="flex h-10 w-10 items-center justify-center rounded-app border-0 bg-transparent text-brown-deep active:bg-[rgba(143,68,32,.08)]"
      >
        <Menu size={22} aria-hidden="true" />
      </button>
      <ActivityListModal isOpen={activityOpen} onClose={() => setActivityOpen(false)} />
      {menuOpen && <NavDrawer sections={sections} activeId={activeId} profile={profile} onClose={closeMenu} />}
    </header>
  );
}

function NavDrawer({
  sections,
  activeId,
  profile,
  onClose,
}: {
  sections: NavSection[];
  activeId?: string;
  profile: Profile;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    panel.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const name = profile.displayName || [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email;

  // Portaled to <body>: the header's backdrop-filter makes it the containing
  // block for `position: fixed` children, which would trap the drawer inside
  // the 56px header.
  return createPortal(
    <div className="fixed inset-0 z-[80] font-body">
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- Escape closes (document listener) */}
      <div role="presentation" className="absolute inset-0 bg-[rgba(0,0,0,.4)]" onClick={onClose} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-[min(300px,86vw)] flex-col bg-sidebar text-sidebar-fg shadow-lg outline-none"
      >
        <div className="flex items-center gap-2.5 border-b border-sidebar-border py-3.5 pl-5 pr-2.5">
          <Logo variant="cream" height={20} />
          <span className="text-[11px] font-bold uppercase tracking-[.14em] text-orange">Admin</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-app border-0 bg-transparent text-cream active:opacity-80"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Admin" className="flex flex-1 flex-col gap-4 overflow-y-auto px-2.5 py-3.5">
          {sections.map((section) => (
            <div key={section.label} className="flex flex-col gap-0.5">
              <div className="px-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-[.14em] text-orange">
                {section.label}
              </div>
              {section.items.map((item) => {
                const Icon = item.icon;
                const row = 'flex w-full items-center gap-2.5 rounded-app px-2.5 py-2.5 text-sm no-underline';
                if (!item.mobile) {
                  return (
                    <div
                      key={item.id}
                      aria-disabled="true"
                      title="Desktop only"
                      className={cx(row, 'font-medium text-[rgba(241,237,225,.34)]')}
                    >
                      <Icon size={17} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      <Monitor size={13} aria-label="Desktop only" className="shrink-0" />
                    </div>
                  );
                }
                const active = item.id === activeId;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={onClose}
                    aria-current={active ? 'page' : undefined}
                    className={cx(
                      row,
                      active
                        ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg hover:text-sidebar-active-fg'
                        : 'font-medium text-[rgba(241,237,225,.78)] hover:text-[rgba(241,237,225,.78)] active:bg-sidebar-hover-bg',
                    )}
                  >
                    <Icon size={17} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-sidebar-border px-2.5 py-3">
          <div className="px-2.5 pb-2">
            <div className="truncate text-[13px] font-semibold text-cream">{name}</div>
            <div className="text-[11px] text-sidebar-fg-muted">{tierLabel(profile.roles)}</div>
          </div>
          <Link
            to="/account"
            onClick={onClose}
            className="flex items-center gap-2.5 rounded-app px-2.5 py-2.5 text-sm font-medium text-[rgba(241,237,225,.78)] no-underline hover:text-[rgba(241,237,225,.78)] active:bg-sidebar-hover-bg"
          >
            <UserRound size={17} aria-hidden="true" /> Account
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2.5 rounded-app border-0 bg-transparent px-2.5 py-2.5 text-left text-sm font-medium text-[rgba(241,237,225,.78)] active:bg-sidebar-hover-bg"
          >
            <LogOut size={17} aria-hidden="true" /> Sign out
          </button>
          <div className="px-2.5 pt-1.5 text-xs text-sidebar-fg-muted">v{import.meta.env.VITE_VERSION}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
